import { FC, useEffect, useState, Fragment } from "react";
import { lddGlobalSessionInfo, NavObjectType, ProvincesShortCode } from "../navigationTypes";
import { PSM } from "../../util/rpwTypes";
import Badge from 'react-bootstrap/Badge';
import CopyFileOption from "./CopyFileOption";
import Dropdown from 'react-bootstrap/Dropdown';
import List, { ListName } from "../List/List";
import { useAppSelector } from "../../redux/hooks";
import "./fileList.css";

export type FileListItem = {
    AppID: number;
    MatterNumber: string;
    PSM: PSM;
    LawyerID: string;
    ClosingDate: string;
    ClientName: string;
    LawyerName: string;
    PropertyType: string;
    FirmContact: string;
    PropertyAddress: string;
    ContractDate: string;
    RequisitionDate: string;
    ReLine: string;
}

export type FileListColumn = {
    attrName: string;
    dataType: string;
    width: string;
    title: string;
}

type FileListProps = {
    listName?: ListName;
    onSelectFile?: (appID: number) => void;
    createNewFile?: () => void;
}

const FileList: FC<FileListProps> = (props) => {
    const SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);
    useEffect(() => {
        async function fetchCustomColumn() {
            const res = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/mainList?SessionID=${SESSION_INFO.sid}`);
            if (res.status !== 200) {
                return;
            }
            const mainlist = await res.json();
            if (mainlist && mainlist.result && mainlist.result.Column && mainlist.result.Column.length > 0) {
                mainlist.result.Column.push({
                    attrName: "Archived",
                    dataType: '',
                    width: '',
                    title: 'Status'
                })
                setListColumns(mainlist.result.Column);
            }
        }
        if (props.listName !== 'SelectFileList' && props.listName !== "DRFileList") {
            fetchCustomColumn();
        }
    }, []);
    const [showCopyOption, setShowCopyOption] = useState<boolean>(false);
    const [copyAppID, setCopyAppID] = useState<number>(-1);
    const [listColumns, setListColumns] = useState<FileListColumn[]>(
        [
            {
                attrName: "MatterNumber",
                dataType: '',
                width: '',
                title: 'File No.'
            },
            {
                attrName: "ClientName",
                dataType: '',
                width: '',
                title: 'Client'
            },
            {
                attrName: "PropertyAddress",
                dataType: '',
                width: '',
                title: 'Address'
            },
            {
                attrName: "PSM",
                dataType: '',
                width: '',
                title: 'Type'
            },
            {
                attrName: "ClosingDate",
                dataType: '',
                width: '',
                title: 'Closing'
            },
            {
                attrName: "Archived",
                dataType: '',
                width: '',
                title: 'Status'
            }
        ]);
    const views = ['Standard View', 'Files View', 'Key Dates View'];
    const [selectedView, setSelectedView] = useState<string>(localStorage.getItem('selectedView') || 'Standard');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const handleDropdownToggle = (isOpen) => {
        setIsDropdownOpen(isOpen);
    };

    useEffect(() => {
        localStorage.setItem('selectedView', selectedView);
    }, [selectedView]);

    function buildFileListLink(
        columns: FileListColumn[],
        nav: (frame: NavObjectType) => void,
        showCopyOption: boolean,
        setShowCopyOption: (val: boolean) => void,
        setCopyAppID: (val: number) => void,
        highlightText: string,
        isArchivedList: boolean,
        archiveFileHandler: (val: number) => void
    ) {
        return (fileEntry: FileListItem) => {
            const navigation: NavObjectType = OpenNewFileDefinition(fileEntry["PSM"], fileEntry['AppID']);
            function onClickFile(appID: number) {
                if (props.onSelectFile) {
                    return props.onSelectFile(appID);
                }
                return nav(navigation);
            }
            return (
                <tr onClick={() => { onClickFile(fileEntry['AppID']) }} key={fileEntry['AppID']}>
                    {columns.map(col => {
                        if (col.attrName === "PSM") {
                            return (
                                <td key={`${fileEntry['AppID']}-${col.attrName}`}>
                                    <Badge bg="info" text="dark" pill>
                                        {fileEntry[col.attrName] === "P" ? "Purchase" : (fileEntry[col.attrName] === "M" ? "Mortgage" : (fileEntry[col.attrName] === "S" ? "Sale" : "Polaris"))}
                                    </Badge>
                                </td>
                            )
                        } else if (col.attrName === "ClosingDate" || col.attrName === "InterimClosingDate") {
                            return (
                                <td key={`${fileEntry['AppID']}-${col.attrName}`}>
                                    <Badge bg="info" text="dark" pill>
                                        {(fileEntry[col.attrName])}
                                    </Badge>
                                </td>
                            )
                        } else if (col.attrName === "Archived") {
                            return (
                                <td key={`${fileEntry['AppID']}-${col.attrName}`}>
                                    <Badge bg={(fileEntry[col.attrName] === null || fileEntry[col.attrName] !== "1" ? "success" : "secondary")} text="dark" pill>
                                        {(fileEntry[col.attrName] === null || fileEntry[col.attrName] !== "1" ? "Active" : "Archived")}
                                    </Badge>
                                </td>
                            )
                        } else if (col.attrName === "MatterNumber") {
                            return (
                                <Fragment  key={`${fileEntry['AppID']}-${col.attrName}`}>
                                    <td colSpan={fileEntry['recentlyAccessed'] ? 1 : 2}>
                                        {getHighlightedText(fileEntry[col.attrName], highlightText)}
                                    </td>
                                    {fileEntry['recentlyAccessed'] && (
                                        <td>
                                            <Badge bg="warning" text="dark" pill>
                                                Recent
                                            </Badge>
                                        </td>
                                    )}
                                </Fragment>
                            );       
                        }
                        return (
                            <td key={`${fileEntry['AppID']}-${col.attrName}`}>
                                {getHighlightedText(fileEntry[col.attrName], highlightText)}
                            </td>
                        )
                    })}
                    <td key={`${fileEntry['AppID']}-options`}>
                        {!props.listName && 
                            <Dropdown onClick={(e) => { e.stopPropagation(); }} align="start">
                                <Dropdown.Toggle variant="icon">
                                    <i className="fa-solid fa-ellipsis-vertical options" title="Options"></i>
                                </Dropdown.Toggle>
        
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={(e) => {
                                        e.stopPropagation();
                                        archiveFileHandler(fileEntry['AppID']);
                                    }}>{fileEntry["Archived"] === null || fileEntry["Archived"] !== "1" ? "Archive" : "Activate"}</Dropdown.Item>
                                    <Dropdown.Item onClick={(e) => {
                                        e.stopPropagation();
                                        setShowCopyOption(!showCopyOption)
                                        setCopyAppID(fileEntry['AppID'])
                                    }}>Duplicate</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        }
                        
                    </td>
                </tr>
            )
        }
    }

    const fileListClsName = () => {
        let clsName = "file-list";
        if (props.listName) {
            clsName += ` ${props.listName}`;
        }
        return clsName
    }

    return (
        <>
            <Dropdown onToggle={handleDropdownToggle}>
                <Dropdown.Toggle className="toggle-height">
                    <i className={`fa-duotone fa-chart-tree-map${isDropdownOpen ? ' dropdown' : ''}`}></i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                <Dropdown.Header>View Options</Dropdown.Header>
                    {views.map((view) => (
                        <Dropdown.Item key={view} onClick={() => setSelectedView(view)} className={selectedView === view ? 'selected-view' : ''}> 
                            {selectedView === view && <i className="fas fa-check" style={{ marginRight:'2px' }}></i>}{view}</Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
            
            <div className={fileListClsName()} style={selectedView === 'Key Dates View' ? { height: "25%" } : selectedView === 'Files View' ? { height: "calc(100% - 70px)" } : {}}>

                <List
                    listContainerID={props.listName ?? null}
                    listName={props.listName ?? "FileList"}
                    FileListColumns={listColumns}
                    showCopyOption={showCopyOption}
                    setShowCopyOption={setShowCopyOption}
                    setCopyAppID={setCopyAppID}
                    handleLoadFileData={fetchData}
                    buildHead={buildHead}
                    buildFileListLink={buildFileListLink}
                    archiveFile={archiveFile}
                    createNewFile={props.createNewFile}
                />
                <CopyFileOption show={showCopyOption} AppID={copyAppID} handleClose={() => { setCopyAppID(-1); setShowCopyOption(false); }} />
            </div>
        </>
    );
}

export interface IAdSearchValue {
    Category?: "Active" | "Archive" | "All";
    PSM?: PSM[];
    MatterNumber?: string;
    Lawyer?: string;
    LSUCID?: string;
    StaffName?: string;
    ClosingDateAfter?: string;
    ClosingDateBefore?: string;
    ClientName?: string;
    PropertyAddress?: string;
    PropertyType?: string;
    ReLineInclude?: string;
    ReLineExclude?: string;
    SolicitorName?: string;
    OtherSideMatterNumber?: string;
}

type TCheckParseReturn = {
    parsedAdSearch: IAdSearchValue | undefined;
    restOfText: string;
};

export function checkParseSearchText(searchText: string): TCheckParseReturn {
    const adSearch: IAdSearchValue = {};
    let restOfText = searchText;
    const regs = {
        PSM: [/type:\s*\(?([P\,?|p\,?|S\,?|s\,?|M\,?|m\,?]*)\)?/i],
        Lawyer: [/[^o]lawyer:\s*\(([^()]*)\)/i, /[^o]lawyer:\s*([\w\d.]*)/i],
        LSUCID: [/LSUCID:\s*\(?([\w\d]*)\)?/i],
        StaffName: [/staff:\s*\(([^()]*)\)/i, /staff:\s*([\w\d.]*)/i],
        ClosingDateAfter: [/from:\s*\(?([\d\-\/\w]*)\)?/i],
        ClosingDateBefore: [/to:\s*\(?([\d\-\/\w]*)\)?/i],
        ClientName: [/client:\s*\(([^()]*)\)/i, /client:\s*([\w\d.]*)/i],
        PropertyAddress: [/address:\s*\(([\w\s\d\.\,\-\']*)\)/i, /address:\s*([\w\d\.\,\-\']*)/i],
        PropertyType: [/property:\s*\(?([\w]*)\)?/i],
        ReLineInclude: [/[^-]reline:\s*\(([^()]*)\)/i, /[^-]reline:\s*([\w\d.]*)/i],
        ReLineExclude: [/-reline:\s*\(([^()]*)\)/i, /-reline:\s*([\w\d.]*)/i],
        SolicitorName: [/olawyer:\s*\(([^()]*)\)/i, /olawyer:\s*([\w\d.]*)/i],
        MatterNumber: [/[^o]matter:\s*\(([\w\s\d]*)\)/i, /[^o]matter:\s*([\w\d]*)/i],
        OtherSideMatterNumber: [/omatter:\s*\(([\w\s\d]*)\)/i, /omatter:\s*([\w\d]*)/i],
        Category: [/status:\s*\(([\w\s\d]*)\)/i, /status:\s*([\w\d]*)/i],
    };
    Object.keys(regs).forEach((k) => {
        for (let i = 0; i < regs[k].length; i++) {
            let regArr = searchText.match(regs[k][i]);
            if (regArr && regArr[1]) {
                if (k === "Category") {
                    if (regArr[1].toLowerCase() === "active" || regArr[1].toLowerCase() === "archive" || regArr[1].toLowerCase() === "all") {
                        adSearch[k] = regArr[1].toLowerCase() === "active" ? "Active" : (regArr[1].toLowerCase() === "archive" ? "Archive" : "All");
                    }
                } else {
                    adSearch[k] = k === "PSM" ? regArr[1].toLocaleUpperCase().split(",") : regArr[1];
                }
                // update restOfText
                const indexInRestText = restOfText.indexOf(regArr[0]);
                if (indexInRestText !== -1) {
                    restOfText = restOfText.substring(0, indexInRestText) + restOfText.substring(indexInRestText + regArr[0].length);
                }
                break;
            }
            else continue;
        }
    });
    // console.log(adSearch)
    return {
        parsedAdSearch: (Object.keys(adSearch).length > 0) ? adSearch : undefined,
        restOfText: restOfText.replaceAll(" ", '') === '' ? '' : restOfText
    };
}

function fetchData(firmId: number, prov: ProvincesShortCode, sid: string, filePage: number, searchText: string, adSearch: IAdSearchValue, setADSearch: (val: IAdSearchValue) => void, sortedColumn?: string, sortingOrder?: boolean): Promise<Response>[] {
    try {
        if (firmId && sid) {
            const { parsedAdSearch, restOfText } = Object.keys(adSearch).length > 0 ? { parsedAdSearch: adSearch, restOfText: "" } : checkParseSearchText(searchText);
            const body = { page: filePage }
            const responsePromises = [];
            if (parsedAdSearch) {
                body["search"] = restOfText;
                for (let k in parsedAdSearch) {
                    body[k] = parsedAdSearch[k];
                }
                if (body["Category"] === undefined) {
                    body["Category"] = "All";
                }
                setADSearch(parsedAdSearch);
            } else {
                body["search"] = searchText;
                if (searchText > "") {
                    body["Category"] = "All"; // include archived in text search
                }
            }

            if (sortedColumn && sortedColumn !== "") {
                body["sortedColumn"] = sortedColumn;
                body["sortingOrder"] = sortingOrder?? true;
            }

            const responseData = fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${prov}/${firmId}/get/fileList?SessionID=${sid}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(body)
            });
            responsePromises.push(responseData);

            if (filePage === 1 && body["search"] !== "") {
                body['freesearch'] = true;
                const freetextSearchResponse = fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${prov}/${firmId}/get/fileList?SessionID=${sid}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(body)
                });
                responsePromises.push(freetextSearchResponse);
            }
            return responsePromises;
        }
        return []
    } catch (e) {
        // console.log(JSON.stringify(e))
        return []
    }
}

function archiveFile(firmId: number, prov: ProvincesShortCode, sid: string, appId: number, toArchive: boolean, callback: () => void) {
    fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${prov}/${firmId}/set/archiveFile?SessionID=${sid}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            "appId": appId,
            "archive": toArchive
        })
    }).then(response => {
        response.json().then(responseData => {
            if (responseData && responseData.ok) {
                callback();
            } else {
                return false;
            }
        })
    })
}

function getHighlightedText(text: string, highlight: string) {
    // Split on highlight term and include term into parts, ignore case
    const highlightText = highlight;
    if (text) {
        highlight = highlight.replace(/\\/g, "");
        highlight = highlight.replace(/\*/g, "\\*");
        highlight = highlight.replace(/\+/g, "\\+");
        highlight = highlight.replace(/\./g, "\\.");
        highlight = highlight.replace(/\?/g, "\\?");
        highlight = highlight.replace(/\[/g, "\\[");
        highlight = highlight.replace(/\]/g, "\\]");
        highlight = highlight.replace(/\{/g, "\\{");
        highlight = highlight.replace(/\}/g, "\\}");
        highlight = highlight.replace(/\(/g, "\\(");
        highlight = highlight.replace(/\)/g, "\\)");
        const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
        return (
            <span>
                {parts.map((part, i) =>
                    <span key={i} style={part.toLowerCase() === highlightText.toLowerCase() ? { fontWeight: 'bold' } : {}}>
                        {part}
                    </span>)
                }
            </span>
        );
    } else {
        return <span></span>;
    }
}

function buildHead (
    columns: FileListColumn[],
    handleClick: (column: string) => void,
    prov: string,
    getSortIcon: (column: string) => JSX.Element
) {
    if (prov === "SK" || prov === "MB") {
        columns = columns.map(col => {
            switch (col.attrName) {
                case "ClosingDate":
                    return {
                        ...col,
                        title: "Possession",
                    }
                case "InterimClosingDate":
                    return {
                        ...col,
                        title: "Interim Possession Date",
                    }
                default:
                    return col;
            }
        });
    }

    return (
        <tr>
            {columns.map(col => {
                return (
                    <th key={`th-${col.attrName}`} data-name={col.attrName} onClick={() => { handleClick(col.attrName) }} colSpan={col.attrName === 'MatterNumber' ? 2 : 1}>
                        {getSortIcon(col.attrName)}
                        {col.title}
                    </th>
                )
            })}
            <th key="options"></th>
        </tr>
    )
}

export function OpenNewFileDefinition(psm: PSM, appid: number): NavObjectType {
    let navigation: NavObjectType = {
        component: 'main',
        url: 'lfmaster.asp',
        params: {
            'formasp': psm === "O" ? 'purFilePolaris' : 'purfileedit',
            'tb': 'TPAppXML',
            'id': appid.toString(),
            'compat': '1',
            'ReactDocument': '-1'
        },
        label: "Basic",
        controller: {
            tabName: "Basic",
            tabNumber: 0,
            refresh: true,
        },
    };
    return navigation;
}

export function createFile(nav: (frame: NavObjectType) => void) {
    const navigation = {
        component: 'main',
        url: 'lfmaster.asp',
        params: {
            'formasp': 'purfilenew',
            "src": "NewLiveFormApp.asp",
            'compat': '1',
        },
    }
    nav(navigation);
}

export function createFileFromTemplate(nav: (frame: NavObjectType) => void) {
    const navigation = {
        component: 'main',
        url: 'appFileFromTemplateXSL.asp',
        params: {
            'compat': '1',
        },
    }
    nav(navigation);
}

export function createFileFromPCLaw(nav: (frame: NavObjectType) => void) {
    const navigation = {
        component: 'main',
        url: 'lfmaster.asp',
        params: {
            'formasp': 'purFileNewBulkCreate',
            "src": "NewLiveFormAppBulkCreate.asp",
            'compat': '1',
        },
    }
    nav(navigation);
}

export default FileList;